import * as supabaseAccess from "@/api/supabaseClient";
import {
  removePersistedAuthSession,
  type AuthSessionStorage,
} from "@/auth/authStorageKey";
import { persistedSessionIsSignedOut } from "@/auth/webSignOutLatch";
import { Platform } from "react-native";

type SignOutOptions = { scope?: "global" | "local" | "others" };
type SignOutResult = { error: { message: string } | null };

function errorText(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message: unknown }).message;
    return message == null ? "" : String(message);
  }
  return "";
}

/** Dieselbe Erkennung wie beim Offline-Gateway: erwartbare Netzfehler, kein Programmierfehler. */
function isExpectedNetworkError(error: unknown): boolean {
  return /failed to fetch|network request failed|fetch failed|timeout|econnreset/i.test(
    errorText(error),
  );
}

/**
 * Normaler Logout. Schlägt das Netz fehl, reicht `signOut({ scope: "local" })`
 * nicht: auth-js ruft für jeden Scope zuerst `admin.signOut` auf und gibt den
 * Fehler zurück, bevor `_removeSession` die Keys löscht.
 * Auf Web werden die Keys danach entfernt, auch wenn `signOut` ohne
 * Fehler zurückkommt: ein Subscriber kann sie in `SIGNED_OUT` wieder schreiben,
 * bevor das Promise resolved. Mit `onlyMatchingLatch` nur, solange der
 * Logout-Latch gilt und die gespeicherte Session noch die alte ist.
 * Nativ bleibt die Session bei einem Netzfehler stehen, damit ein Retry
 * möglich ist und der React-State nicht von `getSession()` abweicht.
 */
export async function revokeSession(input: {
  signOut: (options?: SignOutOptions) => Promise<SignOutResult>;
  storage?: AuthSessionStorage;
  storageKey?: string | null;
  onlyMatchingLatch?: boolean;
}): Promise<void> {
  try {
    const { error } = await input.signOut();
    if (error) {
      // Server-Logout fehlgeschlagen: Refresh-Token bleibt serverseitig gültig.
      console.warn("AuthContext: signOut failed", error.message);
    }
  } catch (err: unknown) {
    const detail = err instanceof Error ? err.message : err;
    if (isExpectedNetworkError(err)) {
      console.warn("AuthContext: signOut threw", detail);
    } else {
      console.error("AuthContext: signOut threw", detail);
    }
  }
  if (Platform.OS !== "web") return;
  try {
    const storage = input.storage ?? supabaseAccess.currentAuthStorage();
    const storageKey =
      input.storageKey === undefined
        ? supabaseAccess.currentAuthStorageKey()
        : input.storageKey;
    if (input.onlyMatchingLatch) {
      if (!storageKey || typeof storage.getItem !== "function") return;
      const sessionRaw = await storage.getItem(storageKey);
      const userRaw = await storage.getItem(`${storageKey}-user`);
      if (
        !persistedSessionIsSignedOut(
          typeof sessionRaw === "string" ? sessionRaw : null,
          typeof userRaw === "string" ? userRaw : null,
        )
      ) {
        return;
      }
    }
    await removePersistedAuthSession(storage, storageKey);
  } catch (err: unknown) {
    console.error(
      "AuthContext: local session remove failed",
      err instanceof Error ? err.message : err,
    );
  }
}
